import { App, Button, Card, Checkbox, Col, Divider, Modal, Row, Select, Space, Spin, Tooltip } from 'antd';
import { intersection, isEqual, isString } from 'lodash';
import moment from 'moment';
import { guid } from 'odata-query';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ENV } from 'src/';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import useMediaQuery from 'src/common/hooks/use-media-query';
import { Country, PermissionsConstants } from 'src/common/types';
import { emptyTableModel, getDateFormat } from 'src/common/utils';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { formatMessage } from 'src/core/services/http.service';
import { Query } from 'src/core/stores/data-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import DatePicker from 'src/core/ui/date-components/date-picker';
import { RangeValue } from 'src/core/ui/date-components/utils';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import { OrganizationSummaryStore } from 'src/stores/organizations-store';
import { isNullOrUndefined, isUndefined } from 'util';
import { DateRangeFilter } from '../../../common/components/table-filters/date-range-filter';
import { OptionsFilter } from '../../../common/components/table-filters/options-filter';
import { isNullOrWhitespace } from '../../../core/utils/object';
import {
  getStructmasterLicenseTableHeaders,
  licenseIsEnabled,
  StructmasterCsvReportRequest,
  StructmasterLicensesStore,
  StructmasterLicenseSummary,
} from '../../../stores/licensing-store';
import StructmasterLicenseGroupEdit from './structmaster-license-group-edit';
import StructmasterLicenseGroupAssignment from './structmaster-license-group-new';
import i18next, { Languages } from 'src/i18n';
import CsvReport from '../reports/csv-report';
import UserSelect from 'src/common/components/business/user-select';
import { useIdentity } from 'src/core/services/authentication';
import { theme } from 'antd';
import { useCurrentUserTenantPermissionsFilter } from 'src/common/permission-hooks';
import { useCheckGlobalLicensesPermission } from 'src/common/hooks/use-check-global-licenses-permission';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';

const { useToken } = theme;
const { RangePicker } = DatePicker;

interface DateFilter {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

const permissions = [PermissionsConstants.ApproveLicenses, PermissionsConstants.ManageLicenses];

const StructmasterLicenseListPage: FC = () => {
  const { t } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);
  const initialQuery = {
    searchQuery: '',
    orderBy: [{ field: 'CreationDate', direction: 'Descending', useProfile: false }],
    filter: {},
    skip: 0,
    take: 10,
  } as Query;
  const [query, setQuery] = useState(initialQuery);

  const structmasterLicensesStore = container.get(StructmasterLicensesStore);
  const structmasterLicensesState = structmasterLicensesStore.state;

  const [newLicenseVisible, setNewLicenseVisible] = useState(false);
  const [licenseVisible, setLicenseVisible] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<{
    id: string;
    disabled: boolean;
    unsigned: boolean;
  }>();

  const { token } = useToken();
  const onMobile = useMediaQuery('(max-width: 768px)');
  const [reportsModal, setReportsModal] = useState(false);

  const [itemModal, setItemModal] = useState<StructmasterCsvReportRequest>({
    headers: getStructmasterLicenseTableHeaders(t),
    csvSeparator: ',',
    currentLanguage: i18next.language !== Languages.Russian ? i18next.language : 'ru-RU',
    fileName: `structmaster_licenses.csv`,
    minutesDifference: moment(new Date()).utcOffset(),
    yesTranslation: t('Yes'),
    noTranslation: t('No'),
    organizationCountryPairs: [],
    activeTranslation: t('Active'),
    notActiveTranslation: t('NotActive'),
  } as StructmasterCsvReportRequest);

  const { notification, modal } = App.useApp();

  const [userFilterValue, setUserFilterValue] = useState<{ value: string; label: string }>();
  const identity = useIdentity();
  const [showOnlyPending, setShowOnlyPending] = useState(false);
  const [userPermissionsSet, checkingPermissions] = useCurrentUserTenantPermissionsFilter(identity, permissions);
  const [hasGlobalLicenseManagerPermission, checkingGlobalLicenseManagerPermission] = useCheckGlobalLicensesPermission();
  const [exportingCsvReport, setExportingCsvReport] = useState(false);

  const canApproveLicenses =
    !checkingPermissions &&
    !checkingGlobalLicenseManagerPermission &&
    (userPermissionsSet.has(PermissionsConstants.ApproveLicenses) || hasGlobalLicenseManagerPermission);

  const hasManageLicensesPermission = !checkingPermissions && userPermissionsSet.has(PermissionsConstants.ManageLicenses);

  setHeader(t('Licenses'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'licenses', breadcrumbName: t('Licenses') },
  ]);
  let getFilterStrFunc = (propName: string): string => {
    let filterStr = (query.filter as any)[propName] as string;
    if (isUndefined(filterStr) || isNullOrWhitespace(filterStr)) return '';
    const result = '[' + filterStr + ']';
    return result;
  };

  const fromDateFilter = (query.filter as any)['fromDate'];
  const hasCreatedOnFilter = !isNullOrUndefined(fromDateFilter) && (!isNullOrUndefined(fromDateFilter.ge) || !isNullOrUndefined(fromDateFilter.le));
  const untilDateFilter = (query.filter as any)['untilDate'];
  const hasExpirationDateFilter =
    !isNullOrUndefined(untilDateFilter) && (!isNullOrUndefined(untilDateFilter.ge) || !isNullOrUndefined(untilDateFilter.le));

  const organizationNameHeader = t('Organization') + '   ' + getFilterStrFunc('OrganizationName');

  const fromDateHeader = t('FromDate') + ' ' + (hasCreatedOnFilter ? '[*]' : '');
  const untilDateHeader = t('UntilDate') + ' ' + (hasExpirationDateFilter ? '[*]' : '');
  const [selectFilterOpen, setSelectFilterOpen] = useState(false);
  const [selectedCustomDate, setSelectedCustomDate] = useState<RangeValue<moment.Moment>>();
  const [selectedDateRangeFilterValue, setSelectedDateRangeFilterValue] = useState<string | undefined>();
  const labelRef = useRef<string | undefined>();
  const activeDatesFilter = useRef<DateFilter>();
  const organizationsCountryFilter = useRef<string>();
  const organizationNameFilter = useRef<string | undefined>();
  const organizationsStore = container.get(OrganizationSummaryStore);
  const organizationsState = organizationsStore.state;
  const organizationIdsFilter = useRef<string[]>();
  const userFilter = useRef<string>();

  const load = async () => {
    await organizationsStore.load({ take: 1000, skip: 0 });
    await structmasterLicensesStore.loadStructmasterLicenses({ query, ids: organizationIdsFilter.current, userId: userFilter.current });
  };

  useEffect(
    () => {
      load();
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const setCustomFilter = (filter: RangeValue<moment.Moment>) => {
    const customLabel = `${filter![0]!.format(getDateFormat())} - ${filter![1]!.format(getDateFormat())}`;
    labelRef.current = customLabel;

    setSelectedCustomDate(filter);

    if (filter !== null) {
      onFilterChange(filter[0], filter[1]);
    } else {
      onFilterChange(null, null);
    }

    setSelectedDateRangeFilterValue(customLabel);
  };

  const onFilterChange = (startDate: moment.Moment | null, endDate: moment.Moment | null) => {
    if (!startDate || !endDate) {
      activeDatesFilter.current = undefined;
      setSelectedDateRangeFilterValue(undefined);
    } else {
      startDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      endDate.add(1, 'day');
      endDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      endDate.subtract(1, 'minute');
      activeDatesFilter.current = { startDate, endDate };
    }
    handleQueryChanged(query);
    setSelectFilterOpen(false);
  };

  const onChangeItem = (separator: string) => {
    let tempItemModal = itemModal;
    tempItemModal.csvSeparator = separator || tempItemModal.csvSeparator;
    setItemModal(tempItemModal);
  };

  const onClearActiveRangeFilter = () => {
    onFilterChange(null, null);
    setSelectedDateRangeFilterValue(undefined);
    setSelectFilterOpen(false);
    activeDatesFilter.current = undefined;
    labelRef.current = undefined;
    handleQueryChanged(query);
    setSelectedCustomDate([null, null]);
  };

  const handleQueryChanged = async (query: Query, onlyPending?: boolean) => {
    if (query.filter && 'organizationId' in query.filter) {
      if (isString((query.filter as any).organizationId)) {
        (query.filter as any).organizationId = guid((query.filter as any).organizationId);
      }
    }

    if (onlyPending !== undefined) {
      if (onlyPending) {
        (query.filter as any)['status'] = 'Pending';
      } else if (query.filter && 'status' in query.filter) {
        delete query.filter.status;
      }
    } else {
      if (query.filter && 'status' in query.filter) {
        setShowOnlyPending(query.filter.status === 'Pending');
      }
    }

    let extraFilters: { status?: string; activeRange?: string } = {};

    const organizationIds = organizationsState.items.value.map((v) => v.id);
    let organizationsFilter = organizationIds;

    if (organizationsCountryFilter.current) {
      let countryOrganizations = organizationsState.items
        .filter((v) => v.country.value === organizationsCountryFilter.current)
        .map((v) => v.id.value);

      organizationsFilter = intersection(countryOrganizations, organizationsFilter);
    }

    if (organizationNameFilter.current) {
      organizationsFilter = intersection(organizationsFilter, [organizationNameFilter.current]);
    }

    // handle date range filter
    if (activeDatesFilter.current?.startDate && activeDatesFilter.current?.endDate) {
      const fromUTC = moment(activeDatesFilter.current.startDate);
      const fromUtcIsoString = fromUTC.toISOString();
      const untilUTC = moment(activeDatesFilter.current.endDate);
      const untilUtcIsoString = untilUTC.toISOString();
      const dateBasedLicensesRangeFilter = `(((creationDate ge ${fromUtcIsoString} and creationDate le ${untilUtcIsoString}) or (expirationDate ge ${fromUtcIsoString} and expirationDate le ${untilUtcIsoString}) or (creationDate le ${fromUtcIsoString} and expirationDate ge ${untilUtcIsoString})))`;
      extraFilters.activeRange = `(${dateBasedLicensesRangeFilter})`;
    } else {
      delete extraFilters.activeRange;
    }

    //parse extra filters
    const filters = Object.values(extraFilters);

    if (filters.length === 0) {
      if ((query.filter as any).and) delete (query.filter as any).and;
    } else {
      (query.filter as any)['and'] = [filters.join(' and ')];
    }

    organizationIdsFilter.current = organizationsFilter.length !== organizationIds.length ? organizationsFilter : undefined;

    setQuery(query);
    await structmasterLicensesStore.loadStructmasterLicenses({ query, ids: organizationIdsFilter.current, userId: userFilter.current });
  };

  const handleExportOk = async () => {
    setExportingCsvReport(true);
    let tempItemModal = {
      ...itemModal,
      headers: getStructmasterLicenseTableHeaders(t),
      currentLanguage: i18next.language !== Languages.Russian ? i18next.language : 'ru-RU',
      fileName: `structmaster_licenses.csv`,
      minutesDifference: moment(new Date()).utcOffset(),
      yesTranslation: t('Yes'),
      noTranslation: t('No'),
      activeTranslation: t('Active'),
      notActiveTranslation: t('NotActive'),
      organizationIds: organizationIdsFilter.current,
      organizationCountryPairs: organizationsState.items.value.map((v) => ({ country: t(v.country), organizationId: v.id })),
    } as StructmasterCsvReportRequest;
    tempItemModal.userId = userFilter.current;
    let response = await structmasterLicensesStore.getReport({ ...query, take: 1000000 }, tempItemModal);
    setExportingCsvReport(false);
    setReportsModal(false);
    if (!response) {
      notification.error({
        placement: 'bottomLeft',
        duration: 20,
        message: t('UnexpectedError'),
      });
    }
  };

  const handleExportCancel = () => {
    setReportsModal(false);
  };

  const getLicenseStatusColor = (license: StructmasterLicenseSummary) => {
    switch (license.status) {
      case 'Active':
        return token.colorSuccess;
      case 'NotActive':
        return token.colorError;
      case 'Pending':
        return token.colorWarning;
    }
  };

  const tableModel = {
    query,
    columns: [
      {
        sortable: true,
        filter: <DateRangeFilter />,
        field: 'creationDate',
        fixed: 'none',
        title: fromDateHeader,
        renderer: (data) => <span>{moment(data.creationDate).format(getDateFormat())}</span>,
      },
      {
        sortable: true,
        filter: <DateRangeFilter />,
        field: 'expirationDate',
        fixed: 'none',
        title: untilDateHeader,
        renderer: (data) => <span>{moment(data.expirationDate).format(getDateFormat())}</span>,
      },
      {
        sortable: false,
        field: 'activeDays',
        fixed: 'none',
        title: t('ActiveDays'),
        renderer: (data) => <span style={{ marginRight: '60px' }}>{data.activeDays}</span>,
      },
      {
        sortable: true,
        applyPartialFilter: true,
        filter: (
          <AutocompleteTextFilter
            baseUrl={`${structmasterLicensesStore.baseUrl}/get-structmaster-licenses`}
            fieldName={'createdByName'}
            fieldValue={'createdByName'}
          />
        ),
        field: 'createdByName',
        fixed: 'none',
        title: t('CreatedBy'),
        renderer: (data) => <span>{data.createdByName}</span>,
      },
      {
        sortable: true,
        applyPartialFilter: true,
        filter: (
          <AutocompleteTextFilter
            baseUrl={`${structmasterLicensesStore.baseUrl}/get-structmaster-licenses`}
            fieldName={'activeUser'}
            fieldValue={'activeUser'}
          />
        ),
        field: 'activeUser',
        fixed: 'none',
        title: t('ActiveUser'),
        renderer: (data) => <span>{data.activeUser}</span>,
      },
      {
        sortable: true,
        field: 'softwareVersion',
        fixed: 'none',
        title: t('SoftwareVersion'),
        renderer: (data) => <span>{data.softwareVersion}</span>,
      },
      {
        sortable: true,
        field: 'licenseName',
        title: t('LicenseName'),
        fixed: 'none',
        applyPartialFilter: true,
        filter: (
          <AutocompleteTextFilter
            baseUrl={`${structmasterLicensesStore.baseUrl}/get-structmaster-licenses`}
            fieldName={'licenseName'}
            fieldValue={'licenseName'}
          />
        ),
        renderer: (data) => <span>{data.licenseName}</span>,
      },
      {
        sortable: false,
        title: organizationNameHeader,
        filter: <OptionsFilter allowSearch options={organizationsState.items.value.map((v) => ({ label: v.displayName, value: v.id }))} />,
        fiterField: 'organizationId',
        field: 'organizationId',
        renderer: (data) => <span>{data.organizationName}</span>,
      },
      {
        sortable: false,
        title: t('Country'),
        filter: (
          <OptionsFilter
            allowSearch
            options={Object.values(Country).flatMap((country) =>
              organizationsState.items.value.find((v) => v.country === country) ? [{ label: t(country), value: country }] : []
            )}
          />
        ),
        fiterField: 'country',
        field: 'country',
        renderer: (data) => <span>{t(data.country)}</span>,
      },
      {
        field: 'status',
        title: t('Status'),
        fiterField: 'status',
        filter: (
          <OptionsFilter
            options={[
              { value: 'Active', label: t('Active') },
              { value: 'NotActive', label: t('NotActive') },
              { value: 'Pending', label: t('ApprovalPending') },
            ]}
          />
        ),
        fixed: 'none',
        renderer: (data) => (
          <span style={{ color: getLicenseStatusColor(data) }}>{data.status === 'Pending' ? t('ApprovalPending') : t(data.status)}</span>
        ),
      },
      {
        sortable: false,
        searchable: false,
        fixed: onMobile ? 'none' : 'right',
        field: '',
        title: t(''),
        renderer: (data) => (
          <span
            className="clickable-span"
            onClick={() => {
              setSelectedLicense({
                id: data.id,
                disabled: !licenseIsEnabled(data.creationDate, data.expirationDate),
                unsigned: data.status === 'Pending',
              });
              setLicenseVisible(true);
            }}
          >
            {t('ShowLicense')}
          </span>
        ),
      },
    ],
    data: { ...structmasterLicensesState.value, isBusy: structmasterLicensesState.value.isBusy },
  } as TableModel<StructmasterLicenseSummary>;

  if (hasManageLicensesPermission || hasGlobalLicenseManagerPermission || canApproveLicenses) {
    tableModel.columns.push({
      sortable: false,
      searchable: false,
      fixed: onMobile ? 'none' : 'right',
      field: '',
      title: t(''),
      renderer: (data) =>
        data.status !== 'Pending' ? (
          hasManageLicensesPermission || hasGlobalLicenseManagerPermission ? (
            <span
              className="clickable-span"
              onClick={async () => {
                await structmasterLicensesStore.unlockLicense(data.id);
                await structmasterLicensesStore.loadStructmasterLicenses({ query, ids: organizationIdsFilter.current, userId: userFilter.current });
              }}
            >
              {t('ResetLicenseAccess')}
            </span>
          ) : (
            ''
          )
        ) : canApproveLicenses || data.createdBy === identity.profile['sub'] ? (
          <span
            className="clickable-span"
            onClick={async () => {
              modal.confirm({
                title: t('DeleteUnsignedLicense'),
                content: t('DeleteUnsignedLicenseWarning').replace('{0}', data.licenseName),
                onOk: async () => {
                  await structmasterLicensesStore.deleteUnsignedLicense(data.id);
                  await structmasterLicensesStore.loadStructmasterLicenses({
                    query,
                    ids: organizationIdsFilter.current,
                    userId: userFilter.current,
                  });
                },
              });
            }}
          >
            {t('DeleteUnsignedLicense')}
          </span>
        ) : (
          ''
        ),
    });
  }

  return (
    <Spin spinning={structmasterLicensesState.value.isBusy}>
      <Row align="top" justify="space-between" className="table-overflow">
        <Card className={`card-table licenses-table`} style={{ width: '100%' }}>
          <Row style={{ width: '100%', padding: '50px 10px 30px 20px' }} gutter={[10, 10]} justify={'space-between'}>
            <Col xs={{ span: 24, order: 2 }} sm={{ span: 24, order: 2 }} md={{ span: 24, order: 2 }} lg={{ span: 12, order: 1 }}>
              <Row style={{ width: '90%' }} gutter={[10, 10]}>
                <Col xl={12} lg={12} sm={24} xs={24}>
                  <Select
                    placement="bottomRight"
                    disabled={
                      structmasterLicensesState.count.value === 0 && (selectedCustomDate === undefined || isEqual(selectedCustomDate, [null, null]))
                    }
                    value={selectedDateRangeFilterValue}
                    allowClear
                    style={{ width: '100%', height: '100%' }}
                    onClear={onClearActiveRangeFilter}
                    placeholder={t('FilterByActiveDates')}
                    open={selectFilterOpen}
                    onOpenChange={(visible) => setSelectFilterOpen(visible)}
                    popupRender={() => (
                      <>
                        <div
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <Divider style={{ margin: '8px 0' }} />
                          <Space style={{ padding: '0 8px 4px' }}>
                            <RangePicker value={selectedCustomDate} onChange={setCustomFilter} allowClear={false} />
                          </Space>
                          <Button
                            type="primary"
                            style={{ width: 'calc(100% - 20px)', height: '40px' }}
                            disabled={!selectedDateRangeFilterValue}
                            onClick={onClearActiveRangeFilter}
                          >
                            {t('ClearFilter')}
                          </Button>
                        </div>
                      </>
                    )}
                  />
                </Col>
                <Col xl={12} lg={12} sm={24} xs={24}>
                  <UserSelect
                    disabled={structmasterLicensesState.count.value === 0 && userFilterValue === undefined}
                    allowEmptyPropValue
                    value={userFilterValue}
                    placeholder={t('FilterByUser')}
                    onChange={(v) => {
                      setUserFilterValue(v);
                      userFilter.current = v?.value;
                      structmasterLicensesStore.loadStructmasterLicenses({
                        query,
                        ids: organizationIdsFilter.current,
                        userId: v?.value,
                      });
                    }}
                  />
                </Col>
                <Col span={24}>
                  <Checkbox
                    style={{ marginTop: 5 }}
                    defaultChecked={false}
                    checked={showOnlyPending}
                    onChange={(e) => {
                      handleQueryChanged(query, e.target.checked);
                      setShowOnlyPending(e.target.checked);
                    }}
                  >
                    {t('ShowOnlyUnsignedLicenses')}
                  </Checkbox>
                </Col>
              </Row>
            </Col>

            <Col xs={{ span: 24, order: 1 }} sm={{ span: 24, order: 1 }} md={{ span: 24, order: 1 }} lg={{ span: 12, order: 2 }}>
              <Row gutter={[10, 10]} justify={'end'}>
                <Col>
                  <Tooltip placement="topLeft" title={t('Export')}>
                    <Button type="primary" onClick={() => setReportsModal(true)}>
                      {t('ExportCSV')}
                    </Button>
                  </Tooltip>
                </Col>

                {((Object.keys(query?.filter ?? {}).length !== 0 && Object.values(query?.filter ?? {}).any((v) => v !== undefined && v !== null)) ||
                  (selectedCustomDate && selectedCustomDate[0] !== null && selectedCustomDate[1] !== null) ||
                  organizationIdsFilter.current?.length ||
                  userFilterValue ||
                  showOnlyPending) && (
                  <Col>
                    <Tooltip placement="topLeft" title={t('ClearFilters')}>
                      <Button
                        type="primary"
                        onClick={() => {
                          organizationIdsFilter.current = [];
                          setShowOnlyPending(false);
                          userFilter.current = undefined;
                          setUserFilterValue(undefined);
                          setSelectedDateRangeFilterValue(undefined);
                          activeDatesFilter.current = undefined;
                          labelRef.current = undefined;
                          setSelectedCustomDate([null, null]);
                          handleQueryChanged(initialQuery);
                        }}
                      >
                        {t('ClearFilters')}
                      </Button>
                    </Tooltip>
                  </Col>
                )}

                {(hasGlobalLicenseManagerPermission || hasManageLicensesPermission) && (
                  <Col>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewLicenseVisible(true)}>
                      {t('AddLicense')}
                    </Button>
                  </Col>
                )}

                <Col>
                  <Button
                    type="default"
                    onClick={() => {
                      structmasterLicensesStore.loadStructmasterLicenses({ query, ids: organizationIdsFilter.current, userId: userFilter.current });
                    }}
                  >
                    <ReloadOutlined style={{ marginRight: 4, marginLeft: 4 }} />
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>

          <TableView
            hideFilter={true}
            hideRefresh
            rowKey="id"
            canEdit={false}
            canSort
            onQueryChanged={handleQueryChanged}
            model={structmasterLicensesState.errorMessage.value ? emptyTableModel : tableModel}
            error={
              structmasterLicensesState.errorMessage.value &&
              (config.environment === ENV.DEV ? formatMessage(structmasterLicensesState.errorMessage.value) : t('LicensesTableError'))
            }
          />
          {newLicenseVisible && (
            <StructmasterLicenseGroupAssignment
              canCreateUnsigned={canApproveLicenses}
              onClose={(reload) => {
                if (reload)
                  structmasterLicensesStore.loadStructmasterLicenses({ query, ids: organizationIdsFilter.current, userId: userFilter.current });
                setNewLicenseVisible(false);
              }}
            />
          )}
          {licenseVisible && selectedLicense && (
            <StructmasterLicenseGroupEdit
              canApproveLicenses={canApproveLicenses}
              unsigned={selectedLicense.unsigned}
              editable={selectedLicense.unsigned}
              disabled={
                selectedLicense.disabled || (!selectedLicense.unsigned && !(hasManageLicensesPermission || hasGlobalLicenseManagerPermission))
              }
              id={selectedLicense.id}
              onClose={(reload) => {
                if (reload)
                  structmasterLicensesStore.loadStructmasterLicenses({ query, ids: organizationIdsFilter.current, userId: userFilter.current });
                setLicenseVisible(false);
              }}
            />
          )}
        </Card>
      </Row>

      {reportsModal && (
        <Modal
          title={<h3>{`${t('Reports')}`}</h3>}
          open={reportsModal}
          onOk={handleExportOk}
          okButtonProps={{ loading: exportingCsvReport }}
          onCancel={handleExportCancel}
          okText={t('Export')}
          cancelText={t('Cancel')}
          cancelButtonProps={{ type: 'default' }}
        >
          <CsvReport onChange={onChangeItem} />
        </Modal>
      )}
    </Spin>
  );
};

export default StructmasterLicenseListPage;
